import CardWrapper from '@/components/common/CardWrapper/CardWrapper';
import { simplifiedSignupSchema } from '@/components/common/UpdateProfile/Schemas';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CustomInput } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

export type SimplifiedSignupSchemaType = z.infer<typeof simplifiedSignupSchema>;

interface SimplifiedSignupProps {
  handleNavigateLogin?: () => void;
  loading: boolean;
}

const SiteUrl = import.meta.env.VITE_SITE_URL;

const SimplifiedSignup: React.FC<SimplifiedSignupProps> = ({
  handleNavigateLogin,
  loading,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SimplifiedSignupSchemaType>();

  return (
    <Tabs className='w-full flex flex-col items-center' defaultValue='customer'>
      <TabsList className='h-12 grid w-full grid-cols-2 bg-[#ffffff29] rounded-full p-1 max-w-[350px]'>
        <TabsTrigger
          className='rounded-full h-10 px-4 py-2 text-xs data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:font-semibold data-[state=active]:shadow-sm'
          value='customer'
        >
          Customer Account
        </TabsTrigger>
        <TabsTrigger
          className='rounded-full h-10 px-4 py-2 text-xs data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:font-semibold data-[state=active]:shadow-sm'
          value='trainer'
        >
          Trainer Account
        </TabsTrigger>
      </TabsList>

      <TabsContent value='customer' className='w-full mt-8 flex flex-col gap-4'>
        <CardWrapper>
          <h4 className='mb-4 text-white'>Personal Information</h4>
          <div className='grid grid-cols-2 gap-5'>
            <CustomInput
              id='create-acc-fname-id'
              {...register('firstName')}
              label='First Name'
              className='rounded-xl'
              error={errors?.firstName?.message}
              requiredItem
            />
            <CustomInput
              id='create-acc-lname-id'
              {...register('lastName')}
              label='Last Name'
              className='rounded-xl'
              error={errors?.lastName?.message}
              requiredItem
            />
          </div>
          <div className='mt-4'>
            <CustomInput
              id='create-acc-email-id'
              {...register('email')}
              label='Email'
              className='rounded-xl'
              error={errors?.email?.message}
              requiredItem
            />
          </div>
          <div className='mt-4'>
            <CustomInput
              id='create-acc-referral-id'
              {...register('referralCode')}
              label='Promo Code'
              className='rounded-xl'
              error={errors?.referralCode?.message}

            />
          </div>
        </CardWrapper>

        <div className='flex items-center space-x-2'>
          <Checkbox
            id='terms'
            checked={watch('terms')}
            onCheckedChange={(checked: any) =>
              setValue('terms', checked as boolean, { shouldValidate: true })
            }
          />
          <label
            htmlFor='terms'
            className='text-sm font-medium leading-5 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white'
          >
            I have read and agreed to the{' '}
            <a href={SiteUrl + 'terms-and-conditions'} target='_blank' rel='noreferrer'>
              <b>Terms & Conditions</b>
            </a>
            {', '}
            <a href={SiteUrl + 'privacy-policy'} target='_blank' rel='noreferrer'>
              <b>Privacy Policy</b>
            </a>{' '}
            and{' '}
            <a
              href={SiteUrl + 'terms-and-conditions/#h-17-limitation-of-liability'}
              target='_blank'
              rel='noreferrer'
            >
              <b>Waiver of Liabilities</b>
            </a>
          </label>
        </div>

        {errors?.terms?.message && (
          <p
            aria-label='error'
            aria-errormessage={errors?.terms?.message}
            className='text-red text-xs'
          >
            {errors?.terms?.message}
          </p>
        )}

        <div className='flex items-center space-x-2'>
          <Checkbox
            id='ageConfirmation'
            checked={watch('ageConfirmation')}
            onCheckedChange={(checked: any) =>
              setValue('ageConfirmation', checked as boolean, { shouldValidate: true })
            }
          />
          <label
            htmlFor='ageConfirmation'
            className='text-sm font-medium leading-5 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white'
          >
            I confirm that I am 13 years of age or older.
          </label>
        </div>

        {errors?.ageConfirmation?.message && (
          <p
            aria-label='error'
            aria-errormessage={errors?.ageConfirmation?.message}
            className='text-red text-xs'
          >
            {errors?.ageConfirmation?.message}
          </p>
        )}

        <Button
          id='create-account-btn'
          size='lg'
          className='w-full mt-2'
          type='submit'
          loading={loading}
        >
          Continue
        </Button>

        {handleNavigateLogin && (
          <h5 className='font-normal mt-2 text-center text-white'>
            Entered the wrong number?{' '}
            <Button
              id='resend-btn'
              onClick={handleNavigateLogin}
              variant='ghost'
              className='p-0 w-min h-min'
            >
              <b>Edit Phone Number.</b>
            </Button>
          </h5>
        )}
      </TabsContent>

      <TabsContent value='trainer'>
        <h5 className='font-normal text-white'>
          Currently, we do a manual review one-on-one to assess trainer qualifications and platform
          requirements. Please send your resume to{' '}
          <b>
            <a href='mailto:info@fitstatic.com'>info@fitstatic.com</a>
          </b>{' '}
          or call us at <br />
          <b>
            <a href='tel:+1-646-887-2690'>+1 (646) 887-2690</a>
          </b>{' '}
          and one of our specialists will onboard you into our platform.
        </h5>
      </TabsContent>
    </Tabs>
  );
};

export default SimplifiedSignup;
